import React, { useCallback } from "react";
import { CrudPage } from "../../components/CrudPage";
import { useAuth } from "../../auth/AuthContext";

// Tipo da forma de pagamento
interface PaymentMethod {
  id: number;
  name: string;
  type: string;
  bankAccountId: number | null;
  creditCardId: number | null;
  allowInstallments: boolean;
  minInstallments: number;
  maxInstallments: number;
  firstPaymentDays: number;
  installmentIntervalDays: number;
  isActive: boolean;
  bankAccount?: {
    id: number;
    bank: { name: string };
    agency: string;
    accountNumber: string;
  } | null;
  creditCard?: {
    id: number;
    name: string;
    brand: string | null;
    last4: string | null;
  } | null;
}

export const PaymentMethodsPage: React.FC = () => {
  const { client } = useAuth();

  // Fetch para contas bancárias
  const fetchBankAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/bank-accounts?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) return [];
      
      const payload = (await response.json()) as {
        data: Array<{
          id: number;
          bank: { name: string };
          agency: string;
          accountNumber: string;
        }>;
      };
      
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.bank.name} - Ag ${item.agency} / CC ${item.accountNumber}`,
      }));
    },
    [client]
  );

  // Fetch para cartões de crédito
  const fetchCreditCards = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/cards?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) return [];
      
      const payload = (await response.json()) as {
        data: Array<{
          id: number;
          name: string;
          brand: string | null;
          last4: string | null;
        }>;
      };
      
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.name}${item.last4 ? ` - Final ${item.last4}` : ""}`,
      }));
    },
    [client]
  );

  // Formatar tipo para exibição
  const formatType = (type: string) => {
    const types: Record<string, string> = {
      DINHEIRO: "Dinheiro",
      TRANSFERENCIA: "Transferência",
      PIX: "PIX",
      BOLETO: "Boleto",
      CARTAO_CREDITO: "Cartão de Crédito",
      CARTAO_DEBITO: "Cartão de Débito",
      CHEQUE: "Cheque",
      OUTROS: "Outros",
    };
    return types[type] || type;
  };

  // Formatar parcelas
  const formatInstallments = (item: PaymentMethod) => {
    if (!item.allowInstallments) return "À vista";
    if (item.minInstallments === item.maxInstallments) {
      return `${item.minInstallments}x`;
    }
    return `${item.minInstallments}x a ${item.maxInstallments}x`;
  };

  return (
    <CrudPage<PaymentMethod>
      title="Formas de Pagamento"
      endpoint="/modules/payment-methods"
      searchPlaceholder="Buscar por nome..."
      columns={[
        { label: "Nome", render: (item) => item.name },
        { label: "Tipo", render: (item) => formatType(item.type) },
        { label: "Parcelas", render: formatInstallments },
        {
          label: "1º Pgto",
          render: (item) =>
            item.firstPaymentDays === 0
              ? "Imediato"
              : `${item.firstPaymentDays} dias`,
        },
        {
          label: "Intervalo",
          render: (item) =>
            item.allowInstallments
              ? `${item.installmentIntervalDays} dias`
              : "-",
        },
        {
          label: "Status",
          render: (item) => (item.isActive ? "Ativo" : "Inativo"),
        },
      ]}
      formFields={[
        {
          name: "name",
          label: "Nome",
          type: "text",
          required: true,
          placeholder: "Ex: PIX à vista, Boleto parcelado, etc.",
        },
        {
          name: "type",
          label: "Tipo",
          type: "select",
          required: true,
          options: [
            { value: "DINHEIRO", label: "Dinheiro" },
            { value: "TRANSFERENCIA", label: "Transferência" },
            { value: "PIX", label: "PIX" },
            { value: "BOLETO", label: "Boleto" },
            { value: "CARTAO_CREDITO", label: "Cartão de Crédito" },
            { value: "CARTAO_DEBITO", label: "Cartão de Débito" },
            { value: "CHEQUE", label: "Cheque" },
            { value: "OUTROS", label: "Outros" },
          ],
        },
        {
          name: "bankAccountId",
          label: "Conta Bancária",
          type: "autocomplete",
          placeholder: "Buscar conta bancária",
          fetchOptions: fetchBankAccounts,
          getIdFromItem: (item) => item.bankAccountId ?? null,
          getLabelFromItem: (item) =>
            item.bankAccount
              ? `${item.bankAccount.bank.name} - Ag ${item.bankAccount.agency} / CC ${item.bankAccount.accountNumber}`
              : "",
        },
        {
          name: "creditCardId",
          label: "Cartão de Crédito",
          type: "autocomplete",
          placeholder: "Buscar cartão",
          fetchOptions: fetchCreditCards,
          getIdFromItem: (item) => item.creditCardId ?? null,
          getLabelFromItem: (item) =>
            item.creditCard
              ? `${item.creditCard.name}${item.creditCard.last4 ? ` - Final ${item.creditCard.last4}` : ""}`
              : "",
        },
        {
          name: "allowInstallments",
          label: "Permite Parcelamento",
          type: "checkbox",
        },
        {
          name: "minInstallments",
          label: "Mínimo de Parcelas",
          type: "number",
          min: 1,
          max: 60,
          placeholder: "1",
        },
        {
          name: "maxInstallments",
          label: "Máximo de Parcelas",
          type: "number",
          min: 1,
          max: 60,
          placeholder: "60",
        },
        {
          name: "firstPaymentDays",
          label: "Dias até 1º Pagamento",
          type: "number",
          min: 0,
          max: 365,
          placeholder: "0 = imediato",
        },
        {
          name: "installmentIntervalDays",
          label: "Intervalo entre Parcelas (dias)",
          type: "number",
          min: 1,
          max: 365,
          placeholder: "30",
        },
        {
          name: "isActive",
          label: "Ativo",
          type: "checkbox",
        },
      ]}
      initialValues={{
        name: "",
        type: "PIX",
        bankAccountId: null,
        creditCardId: null,
        allowInstallments: false,
        minInstallments: 1,
        maxInstallments: 1,
        firstPaymentDays: 0,
        installmentIntervalDays: 30,
        isActive: true,
      }}
      mapToPayload={(values) => ({
        name: values.name,
        type: values.type,
        bankAccountId: values.bankAccountId ? Number(values.bankAccountId) : null,
        creditCardId: values.creditCardId ? Number(values.creditCardId) : null,
        allowInstallments: Boolean(values.allowInstallments),
        minInstallments: Number(values.minInstallments) || 1,
        maxInstallments: Number(values.maxInstallments) || 1,
        firstPaymentDays: Number(values.firstPaymentDays) || 0,
        installmentIntervalDays: Number(values.installmentIntervalDays) || 30,
        isActive: Boolean(values.isActive),
      })}
      permissionWrite="PAYMENT_METHOD_WRITE"
    />
  );
};
